import React from 'react'
import MessageCenterChat from './MessageCenterChat/MessageCenterChat.component'
import MessageCenterCalls from './MessageCenterCalls/MessageCenterCalls.component'
import MessageCenterFax from './MessageCenterFax/MessageCenterFax.component'
import MessageCenterEmail from './MessageCenterEmail/MessageCenterEmail.component'
import MessageCenterContact from './MessageCenterContact/MessageCenterContact.component'
import useMessageCenter from './useMessageCenter'
import ButtonGroup from '@components/common/ButtonGroup/ButtonGroup.component'
import './MessageCenterStyle.css'
import {
  AddContactGroupModal,
  ScheduleAppointment,
} from '@components/layout/Modals'
import AddNoteModal from '@components/layout/Modals/AddNote/AddNoteModal'
import { NoteContextType } from '@dataTypes/note.type'
import { getFullName } from '@utils/fullname.util'
import ManageContactGroupModal from '@components/layout/Modals/ManageContactGroup/ManageContactGroupModal.component'
import MessageCenterDeleteContactModal from './MessageCenterDeleteContactModal.component'
import MessageCenterBlockContactModal from './MessageCenterBlockContactModal.component'
import { MESSAGE_TABS } from '@constants/Message'
import { MessageCenterTabLayerProvider } from './MessageCenterTabLayer.context'
import MessageCenterSkeleton from './MessageCenterSkeleton/MessageCenterSkeleton.component'

const MessageCenter: React.FC = () => {
  const { ...hooks } = useMessageCenter()

  const blockContactName =
    getFullName(hooks.contact, true) ||
    getFullName(hooks.participant?.contact, true) ||
    hooks.participant?.username ||
    hooks.participant?.phone_number ||
    ''

  return (
    <MessageCenterTabLayerProvider value={hooks.tabLayerContextValue}>
      <>
        <div className="mcc-content">
          <div className="mcc-tab-layer">
            <ButtonGroup
              buttons={MESSAGE_TABS}
              activeTab={hooks.activeTab}
              onButtonClick={hooks.handleButtonClick}
              className="custom-button-group"
            />
            {hooks.tabLayerActions}
          </div>
          <div className="mcc-tab-panel">
            {hooks.isTabSkeleton ? (
              <MessageCenterSkeleton
                hideHeader
                variant={
                  hooks.activeTab === 'contact'
                    ? 'contact'
                    : hooks.activeTab === 'calls'
                      ? 'calls'
                      : 'list'
                }
              />
            ) : (
              <>
                {hooks.activeTab === 'chat' && <MessageCenterChat />}
                {hooks.activeTab === 'calls' && <MessageCenterCalls />}
                {hooks.activeTab === 'fax' && <MessageCenterFax />}
                {hooks.activeTab === 'email' && (
                  <MessageCenterEmail
                    onOpenEmailComposer={hooks.openEmailComposer}
                    composeOpen={hooks.emailComposerShow}
                    composeDraft={hooks.emailComposerDraft}
                    onComposeDraftChange={hooks.onEmailComposerDraftChange}
                    onComposeAutoSaveAndClose={
                      hooks.onEmailComposerAutoSaveAndHide
                    }
                    onComposeClose={hooks.onEmailComposerHide}
                  />
                )}
                {hooks.activeTab === 'contact' && <MessageCenterContact />}
              </>
            )}
          </div>
        </div>
        {hooks.showModal.group && (
          <AddContactGroupModal
            show={hooks.showModal.group}
            onHide={hooks.onAddContactGroupModalHide}
            initialData={hooks.selectedContactGroup}
          />
        )}
        <MessageCenterBlockContactModal
          show={hooks.showModal.block}
          contactName={blockContactName}
          onCancel={hooks.onBlockModalHide}
          onBlock={hooks.onConfirmBlockUser}
        />
        <MessageCenterDeleteContactModal
          show={hooks.showModal.delete}
          contactName={getFullName(hooks.contact, true)}
          onCancel={hooks.onDeleteModalHide}
          onDelete={hooks.onConfirmDeleteContact}
        />

        {hooks.showModal.appointment && (
          <ScheduleAppointment
            isOpen={hooks.showModal.appointment}
            handleModal={hooks.onAppointmentModalHide}
            onSave={hooks.onAppointmentSave}
            currentAppt={{
              patient_id: hooks.patient?.patient_id,
              patient: hooks.patient ?? undefined,
            }}
          />
        )}

        {hooks.showModal.addNote && (
          <AddNoteModal
            show={hooks.showModal.addNote}
            onHide={hooks.onAddNoteModalHide}
            source={NoteContextType.CONTACT}
            initialFilter={{ category: NoteContextType.CONTACT }}
          />
        )}
        {hooks.showModal.manageGroup && (
          <ManageContactGroupModal
            show={hooks.showModal.manageGroup}
            onHide={hooks.onManageGroupModalHide}
          />
        )}
      </>
    </MessageCenterTabLayerProvider>
  )
}

export default MessageCenter
